(() => {
  'use strict';

  const VERSION = 3;
  const RUNTIME_REVISION = '54001b87898e34e967c1d05104291986';
  if (window.WUBCommerceLoader?.version === VERSION) return;

  const script = document.currentScript;
  let runtimePromise = null;
  let replaying = false;
  let primeObserver = null;
  let idleHandle = null;

  function sibling(filename, revision = '') {
    if (!script?.src) return '';
    const url = new URL(script.src, window.location.href);
    url.pathname = url.pathname.replace(/[^/]+$/, filename);
    url.search = '';
    url.hash = '';
    if (revision) url.searchParams.set('v', revision);
    return url.href;
  }

  const runtimeSrc = sibling('wub-commerce-runtime.js', RUNTIME_REVISION);
  const pathIsCart = /\/cart\/?$/.test(window.location.pathname);
  const productPage = document.body?.classList.contains('wub-product-v2') === true;

  function commerceReady() {
    return Boolean(window.WUBCommerce?.openDrawer && window.WUBCommerce?.refreshDrawer);
  }

  function stopPriming() {
    primeObserver?.disconnect();
    primeObserver = null;
    if (idleHandle == null) return;
    if ('cancelIdleCallback' in window) window.cancelIdleCallback(idleHandle);
    else window.clearTimeout(idleHandle);
    idleHandle = null;
  }

  function load() {
    if (commerceReady()) {
      stopPriming();
      return Promise.resolve(true);
    }
    if (!runtimeSrc) return Promise.resolve(false);
    if (runtimePromise) return runtimePromise;

    stopPriming();
    runtimePromise = new Promise((resolve) => {
      const existing = document.querySelector('script[data-wub-commerce-runtime]');
      if (existing) {
        if (commerceReady()) {
          resolve(true);
          return;
        }
        existing.addEventListener('load', () => resolve(commerceReady()), { once: true });
        existing.addEventListener('error', () => resolve(false), { once: true });
        return;
      }

      const node = document.createElement('script');
      node.src = runtimeSrc;
      node.async = true;
      node.dataset.wubCommerceRuntime = 'true';
      node.addEventListener('load', () => resolve(commerceReady()), { once: true });
      node.addEventListener('error', () => {
        runtimePromise = null;
        resolve(false);
      }, { once: true });
      document.head.appendChild(node);
    });

    return runtimePromise;
  }

  function cartAddForm(target) {
    const form = target?.closest?.('form');
    if (!form) return null;
    const action = form.getAttribute('action') || '';
    return action.includes('/cart/add') ? form : null;
  }

  function commerceIntent(target) {
    return Boolean(target?.closest?.(
      '[data-wub-cart-open], form[action*="/cart/add"]'
    ));
  }

  function nativeSubmit(form) {
    try {
      HTMLFormElement.prototype.submit.call(form);
    } catch (_) {
      window.location.assign(window.WUB?.routes?.cart_url || '/cart');
    }
  }

  function setBootFeedback(submitter) {
    if (!submitter) return () => {};
    const wasDisabled = submitter.disabled;
    const hadBusy = submitter.hasAttribute('aria-busy');
    const previousBusy = submitter.getAttribute('aria-busy');

    if (!wasDisabled) submitter.disabled = true;
    submitter.setAttribute('aria-busy', 'true');

    return () => {
      submitter.disabled = wasDisabled;
      if (hadBusy) submitter.setAttribute('aria-busy', previousBusy || 'true');
      else submitter.removeAttribute('aria-busy');
    };
  }

  async function openCart(trigger, { refresh = false, added = false } = {}) {
    const ready = await load();
    if (!ready) return false;

    const commerce = window.WUBCommerce;
    if (!commerce?.openDrawer) return false;

    if (refresh && commerce.refreshDrawer) {
      const refreshed = await commerce.refreshDrawer();
      if (!refreshed) return false;
    }

    return commerce.openDrawer({ trigger, added }) === true;
  }

  function primeNearCommerce() {
    if (commerceReady() || pathIsCart) return;

    if (productPage) {
      load();
      return;
    }

    const targets = Array.from(document.querySelectorAll(
      'form[action*="/cart/add"]'
    ));

    if ('IntersectionObserver' in window && targets.length) {
      primeObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        load();
      }, { rootMargin: '900px 0px' });
      targets.forEach((target) => primeObserver.observe(target));
    }
  }

  document.addEventListener('pointerover', (event) => {
    if (!commerceReady() && commerceIntent(event.target)) load();
  }, { capture: true, passive: true });

  document.addEventListener('pointerdown', (event) => {
    if (!commerceReady() && commerceIntent(event.target)) load();
  }, { capture: true, passive: true });

  document.addEventListener('focusin', (event) => {
    if (!commerceReady() && commerceIntent(event.target)) load();
  }, true);

  document.addEventListener('submit', (event) => {
    if (replaying || commerceReady()) return;
    const form = cartAddForm(event.target);
    if (!form) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const submitter = event.submitter && event.submitter.form === form ? event.submitter : null;
    const restoreFeedback = setBootFeedback(submitter);

    load().then((ready) => {
      restoreFeedback();

      if (!ready || !form.isConnected) {
        nativeSubmit(form);
        return;
      }

      replaying = true;
      try {
        if (typeof form.requestSubmit === 'function') form.requestSubmit(submitter || undefined);
        else nativeSubmit(form);
      } finally {
        replaying = false;
      }
    });
  }, true);

  document.addEventListener('click', (event) => {
    if (replaying || commerceReady()) return;

    const cartTrigger = event.target?.closest?.('[data-wub-cart-open]');
    if (!cartTrigger) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    openCart(cartTrigger).then((opened) => {
      if (!opened) window.location.assign(cartTrigger.href || window.WUB?.routes?.cart_url || '/cart');
    });
  }, true);

  document.addEventListener('cart:open', (event) => {
    if (commerceReady()) return;
    const trigger = event.detail?.trigger || null;
    openCart(trigger, { refresh: true, added: Boolean(event.detail?.added) });
  });

  ['cart:updated', 'cart:update', 'cart:refresh', 'cart:item-added', 'line-item:change:end'].forEach((name) => {
    document.addEventListener(name, () => {
      if (!commerceReady()) load();
    }, { passive: true });
  });

  if (pathIsCart) load();
  else primeNearCommerce();

  window.WUBCommerceLoader = Object.freeze({
    version: VERSION,
    load,
    openCart
  });
})();
